import { Vector3 } from 'three/webgpu';
import type { HollowDef } from './HollowDef';

/**
 * Where a tunnel pierces a hollow wall. Near the wall the tunnel is a straight
 * cylinder (axis through `point` along `dir`, which points *into the rock*), so
 * hole cutting in shaders and collision can use exact analytic tests.
 */
export interface TunnelMouth {
  hollow: HollowDef;
  /** Point on the tunnel axis where it crosses the reference wall (universe). */
  point: Vector3;
  /** Unit axis direction pointing from the hollow into the rock. */
  dir: Vector3;
  radius: number;
}

export interface TunnelDef {
  id: string;
  name: string;
  radius: number;
  /** Dense centre-line in universe space (f64). */
  path: Vector3[];
  mouths: TunnelMouth[];
}

const _o = new Vector3();

/**
 * Parameter t along the mouth axis where the line parallel to the axis at
 * `offset` (perpendicular) hits the hollow wall, or NaN. Picks the root nearest
 * the mouth point.
 */
export function wallCrossing(mouth: TunnelMouth, offset: Vector3, wallRadius: number): number {
  _o.copy(mouth.point).add(offset).sub(mouth.hollow.center);
  const b = _o.dot(mouth.dir);
  const disc = b * b - (_o.lengthSq() - wallRadius * wallRadius);
  if (disc < 0) return Number.NaN;
  const s = Math.sqrt(disc);
  const t1 = -b + s;
  const t2 = -b - s;
  return Math.abs(t1) < Math.abs(t2) ? t1 : t2;
}

/** Orthonormal basis (n, b) perpendicular to unit vector d. */
export function perpendicularBasis(d: Vector3, n: Vector3, b: Vector3): void {
  const helper = Math.abs(d.y) < 0.9 ? new Vector3(0, 1, 0) : new Vector3(1, 0, 0);
  n.crossVectors(helper, d).normalize();
  b.crossVectors(d, n).normalize();
}

function axisBetween(a: HollowDef, b: HollowDef): { axis: Vector3; pa: Vector3; pb: Vector3; gap: number } {
  const axis = b.center.clone().sub(a.center);
  const dist = axis.length();
  axis.divideScalar(dist);
  const pa = a.center.clone().addScaledVector(axis, a.radius);
  const pb = b.center.clone().addScaledVector(axis, -b.radius);
  return { axis, pa, pb, gap: dist - a.radius - b.radius };
}

/** A straight vertical shaft between two hollows: fall in, float at the saddle. */
export function buildDropShaft(a: HollowDef, b: HollowDef, radius: number): TunnelDef {
  const { axis, pa, pb, gap } = axisBetween(a, b);
  const start = pa.clone().addScaledVector(axis, -3);
  const len = gap + 6;
  const path: Vector3[] = [];
  const steps = Math.ceil(len / 1.0);
  for (let i = 0; i <= steps; i++) path.push(start.clone().addScaledVector(axis, (i / steps) * len));
  return {
    id: `shaft-${a.id}-${b.id}`,
    name: 'Drop shaft',
    radius,
    path,
    mouths: [
      { hollow: a, point: pa.clone(), dir: axis.clone(), radius },
      { hollow: b, point: pb.clone(), dir: axis.clone().negate(), radius },
    ],
  };
}

export interface SpiralOptions {
  tubeRadius: number;
  helixRadius: number;
  /** Slope of the walkway relative to the local horizontal, degrees. */
  pitchDeg: number;
  /** Depth below each wall where the helix begins/ends. */
  depth: number;
  /** Where around the axis the tunnel starts (radians). */
  phase: number;
}

/**
 * A helical walkway around the axis between two hollows. Near each end gravity
 * points along the axis, so the helix pitch is the walking slope. Straight
 * segments tangent to the helix rise out through each wall.
 */
export function buildSpiralTunnel(a: HollowDef, b: HollowDef, o: SpiralOptions): TunnelDef {
  const { axis, pa, gap } = axisBetween(a, b);
  const n = new Vector3();
  const bb = new Vector3();
  perpendicularBasis(axis, n, bb);
  const psi = (o.pitchDeg * Math.PI) / 180;
  const axialLen = gap - 2 * o.depth;
  const turns = axialLen / (2 * Math.PI * o.helixRadius * Math.tan(psi));
  const thetaEnd = o.phase + turns * 2 * Math.PI;
  const helixPoint = (theta: number, out: Vector3) => {
    const along = o.depth + ((theta - o.phase) / (thetaEnd - o.phase)) * axialLen;
    return out
      .copy(pa)
      .addScaledVector(axis, along)
      .addScaledVector(n, o.helixRadius * Math.cos(theta))
      .addScaledVector(bb, o.helixRadius * Math.sin(theta));
  };
  const helixTangent = (theta: number, out: Vector3) =>
    out
      .copy(n)
      .multiplyScalar(-Math.sin(theta) * Math.cos(psi))
      .addScaledVector(bb, Math.cos(theta) * Math.cos(psi))
      .addScaledVector(axis, Math.sin(psi))
      .normalize();

  // Straight entry: back up along the start tangent until the tube *floor* (not just its
  // centre line) is above wall A, otherwise the walkway ends below ground.
  const h0 = helixPoint(o.phase, new Vector3());
  const t0 = helixTangent(o.phase, new Vector3());
  const entryLen = (o.depth + o.tubeRadius / Math.cos(psi) + 1.5) / Math.sin(psi);
  const h1 = helixPoint(thetaEnd, new Vector3());
  const t1 = helixTangent(thetaEnd, new Vector3());
  const exitLen = (o.depth + o.tubeRadius / Math.cos(psi) + 1.5) / Math.sin(psi);

  const path: Vector3[] = [];
  // Fine rings near the mouths keep the wall/tube intersection tight.
  const pushLine = (from: Vector3, dir: Vector3, len: number, step: number, reverse: boolean) => {
    const count = Math.ceil(len / step);
    for (let i = 0; i < count; i++) {
      const s = reverse ? len - (i / count) * len : (i / count) * len;
      path.push(from.clone().addScaledVector(dir, reverse ? -s : s));
    }
  };
  pushLine(h0, t0, entryLen, 0.25, true);
  const helixLen = axialLen / Math.sin(psi);
  const hSteps = Math.ceil(helixLen / 1.0);
  for (let i = 0; i <= hSteps; i++) {
    path.push(helixPoint(o.phase + (i / hSteps) * (thetaEnd - o.phase), new Vector3()));
  }
  const exitCount = Math.ceil(exitLen / 0.25);
  for (let i = 1; i <= exitCount; i++) path.push(h1.clone().addScaledVector(t1, (i / exitCount) * exitLen));

  // Mouth points: where each straight segment's axis crosses the reference wall.
  const mouthA: TunnelMouth = { hollow: a, point: h0.clone(), dir: t0.clone(), radius: o.tubeRadius };
  const ta = wallCrossing(mouthA, new Vector3(), a.radius);
  mouthA.point.addScaledVector(t0, ta);
  const mouthB: TunnelMouth = {
    hollow: b,
    point: h1.clone(),
    dir: t1.clone().negate(),
    radius: o.tubeRadius,
  };
  const tb = wallCrossing(mouthB, new Vector3(), b.radius);
  mouthB.point.addScaledVector(mouthB.dir, tb);

  return {
    id: `spiral-${a.id}-${b.id}`,
    name: 'Spiral tunnel',
    radius: o.tubeRadius,
    path,
    mouths: [mouthA, mouthB],
  };
}

export interface TubeData {
  positions: Float32Array;
  normals: Float32Array;
  uvs: Float32Array;
  indices: Uint32Array;
}

/**
 * Inward-facing tube along `path` using parallel-transport frames. Positions
 * are relative to `anchor` (so they are small, precise f32 numbers). `radius` is
 * one for the whole tube, or one per path point.
 */
export function buildTube(
  path: Vector3[],
  radius: number | ArrayLike<number>,
  radialSegments: number,
  anchor: Vector3,
): TubeData {
  const rings = path.length;
  const cols = radialSegments + 1;
  const positions = new Float32Array(rings * cols * 3);
  const normals = new Float32Array(rings * cols * 3);
  const uvs = new Float32Array(rings * cols * 2);
  const t = new Vector3();
  const nrm = new Vector3();
  const bin = new Vector3();
  const dir = new Vector3();
  let arc = 0;
  for (let i = 0; i < rings; i++) {
    const p0 = path[Math.max(i - 1, 0)];
    const p1 = path[Math.min(i + 1, rings - 1)];
    t.subVectors(p1, p0).normalize();
    if (i === 0) {
      perpendicularBasis(t, nrm, bin);
    } else {
      // Parallel transport: remove the component along the new tangent.
      nrm.addScaledVector(t, -nrm.dot(t)).normalize();
      bin.crossVectors(t, nrm).normalize();
      arc += path[i].distanceTo(path[i - 1]);
    }
    const ri = typeof radius === 'number' ? radius : radius[i];
    for (let j = 0; j < cols; j++) {
      const theta = (j / radialSegments) * Math.PI * 2;
      dir.copy(nrm).multiplyScalar(Math.cos(theta)).addScaledVector(bin, Math.sin(theta));
      const k = (i * cols + j) * 3;
      positions[k] = path[i].x - anchor.x + ri * dir.x;
      positions[k + 1] = path[i].y - anchor.y + ri * dir.y;
      positions[k + 2] = path[i].z - anchor.z + ri * dir.z;
      normals[k] = -dir.x;
      normals[k + 1] = -dir.y;
      normals[k + 2] = -dir.z;
      const u = (i * cols + j) * 2;
      uvs[u] = arc / (2 * Math.PI * ri);
      uvs[u + 1] = j / radialSegments;
    }
  }
  const indices = new Uint32Array((rings - 1) * radialSegments * 6);
  let q = 0;
  for (let i = 0; i < rings - 1; i++) {
    for (let j = 0; j < radialSegments; j++) {
      const a = i * cols + j;
      const b = (i + 1) * cols + j;
      const c = i * cols + j + 1;
      const d = (i + 1) * cols + j + 1;
      // (a, b, c) winds counter-clockwise when seen from inside the tube.
      indices[q++] = a;
      indices[q++] = b;
      indices[q++] = c;
      indices[q++] = b;
      indices[q++] = d;
      indices[q++] = c;
    }
  }
  return { positions, normals, uvs, indices };
}
